'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Bell, CheckCircle2, Clock, XCircle, MessageSquare, TrendingUp, IndianRupee, Filter, CheckCheck, ArrowLeft, Loader2, AlertCircle, Sparkles,  } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DirectoryTopbar from '@/app/loan-aggregator-directory/components/DirectoryTopbar';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type NotificationType =
  | 'status_update' | 'approval' | 'rejection' | 'lender_message' | 'under_review' | 'new_offer';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  loanType?: string;
  aggregatorName?: string;
  amount?: string;
  applicationId?: string;
}

type FilterTab = 'all' | 'unread' | 'status' | 'messages';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getNotificationConfig(type: NotificationType) {
  switch (type) {
    case 'approval':
      return {
        icon: <CheckCircle2 size={18} />,
        iconBg: 'bg-green-100 text-green-600',
        dot: 'bg-green-500',
        accent: 'border-l-green-500',
      };
    case 'rejection':
      return {
        icon: <XCircle size={18} />,
        iconBg: 'bg-red-100 text-red-500',
        dot: 'bg-red-500',
        accent: 'border-l-red-400',
      };
    case 'under_review':
      return {
        icon: <Clock size={18} />,
        iconBg: 'bg-amber-100 text-amber-600',
        dot: 'bg-amber-500',
        accent: 'border-l-amber-400',
      };
    case 'lender_message':
      return {
        icon: <MessageSquare size={18} />,
        iconBg: 'bg-blue-100 text-blue-600',
        dot: 'bg-blue-500',
        accent: 'border-l-blue-500',
      };
    case 'new_offer':
      return {
        icon: <Sparkles size={18} />,
        iconBg: 'bg-purple-100 text-purple-600',
        dot: 'bg-purple-500',
        accent: 'border-l-purple-400',
      };
    default:
      return {
        icon: <TrendingUp size={18} />,
        iconBg: 'bg-primary/10 text-primary',
        dot: 'bg-primary',
        accent: 'border-l-primary',
      };
  }
}

function formatRelativeTime(isoString: string): string {
  const now = new Date();
  const date = new Date(isoString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function groupByDate(notifications: Notification[]): { label: string; items: Notification[] }[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 7 * 86400000);

  const groups: Record<string, Notification[]> = {
    Today: [],
    Yesterday: [],
    'This Week': [],
    Earlier: [],
  };

  notifications.forEach((n) => {
    const d = new Date(n.timestamp);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (day.getTime() === today.getTime()) groups['Today'].push(n);
    else if (day.getTime() === yesterday.getTime()) groups['Yesterday'].push(n);
    else if (day >= weekAgo) groups['This Week'].push(n);
    else groups['Earlier'].push(n);
  });

  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

// ─── Build notifications from real loan application data ──────────────────────

function buildNotificationsFromApplications(applications: any[], aggregators: any[]): Notification[] {
  const aggregatorMap = new Map(aggregators.map((a) => [a.id, a]));
  const notifications: Notification[] = [];

  applications.forEach((app) => {
    const agg = aggregatorMap.get(app.aggregator_id);
    const aggName = agg?.name || 'Lender';

    // Application submitted notification
    notifications.push({
      id: `${app.id}-applied`,
      type: 'status_update',
      title: 'Application Submitted',
      message: `Your ${app.loan_type} application of ${app.amount || 'an amount'} was submitted to ${aggName}.`,
      timestamp: app.applied_at,
      read: true,
      loanType: app.loan_type,
      aggregatorName: aggName,
      amount: app.amount,
      applicationId: app.id,
    });

    // Status-specific notification
    if (app.status === 'Under Review') {
      notifications.push({
        id: `${app.id}-review`,
        type: 'under_review',
        title: 'Application Under Review',
        message: `${aggName} is reviewing your ${app.loan_type} application. Expect a response within 2–3 business days.`,
        timestamp: new Date(new Date(app.applied_at).getTime() + 3600000).toISOString(),
        read: false,
        loanType: app.loan_type,
        aggregatorName: aggName,
        applicationId: app.id,
      });
    } else if (app.status === 'Approved') {
      notifications.push({
        id: `${app.id}-approved`,
        type: 'approval',
        title: 'Loan Approved! 🎉',
        message: `Congratulations! Your ${app.loan_type} application with ${aggName} has been approved.`,
        timestamp: app.updated_at || new Date(new Date(app.applied_at).getTime() + 86400000).toISOString(),
        read: false,
        loanType: app.loan_type,
        aggregatorName: aggName,
        amount: app.amount,
        applicationId: app.id,
      });
      notifications.push({
        id: `${app.id}-msg`,
        type: 'lender_message',
        title: `Message from ${aggName}`,
        message: `Please complete your KYC verification to disburse your approved ${app.loan_type}. Upload your documents at the earliest.`,
        timestamp: app.updated_at || new Date(new Date(app.applied_at).getTime() + 90000000).toISOString(),
        read: false,
        loanType: app.loan_type,
        aggregatorName: aggName,
        applicationId: app.id,
      });
    } else if (app.status === 'Rejected') {
      notifications.push({
        id: `${app.id}-rejected`,
        type: 'rejection',
        title: 'Application Not Approved',
        message: `Your ${app.loan_type} application with ${aggName} was not approved at this time. You may reapply after 30 days.`,
        timestamp: app.updated_at || new Date(new Date(app.applied_at).getTime() + 86400000).toISOString(),
        read: false,
        loanType: app.loan_type,
        aggregatorName: aggName,
        applicationId: app.id,
      });
    }
  });

  // Add a generic new offer notification if user has applications
  if (applications.length > 0) {
    notifications.push({
      id: 'offer-1',
      type: 'new_offer',
      title: 'New Loan Offer Available',
      message: 'Based on your profile, you qualify for a pre-approved Personal Loan at 10.5% p.a. Check your matched lenders.',
      timestamp: new Date(Date.now() - 2 * 3600000).toISOString(),
      read: false,
    });
  }

  return notifications.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

// ─── Static demo notifications (shown when no real data) ─────────────────────

const DEMO_NOTIFICATIONS: Notification[] = [
  {
    id: 'demo-1',
    type: 'new_offer',
    title: 'New Loan Offer Available',
    message: 'Based on your profile, you qualify for a pre-approved Personal Loan at 10.5% p.a. Check your matched lenders.',
    timestamp: new Date(Date.now() - 1 * 3600000).toISOString(),
    read: false,
  },
  {
    id: 'demo-2',
    type: 'approval',
    title: 'Loan Approved! 🎉',
    message: 'Congratulations! Your Home Loan application with BankBazaar has been approved for ₹45,00,000.',
    timestamp: new Date(Date.now() - 5 * 3600000).toISOString(),
    read: false,
    loanType: 'Home Loan',
    aggregatorName: 'BankBazaar',
    amount: '₹45,00,000',
  },
  {
    id: 'demo-3',
    type: 'lender_message',
    title: 'Message from BankBazaar',
    message: 'Please complete your KYC verification to disburse your approved Home Loan. Upload your documents at the earliest.',
    timestamp: new Date(Date.now() - 6 * 3600000).toISOString(),
    read: false,
    aggregatorName: 'BankBazaar',
  },
  {
    id: 'demo-4',
    type: 'under_review',
    title: 'Application Under Review',
    message: 'Paisabazaar is reviewing your Business Loan application. Expect a response within 2–3 business days.',
    timestamp: new Date(Date.now() - 26 * 3600000).toISOString(),
    read: true,
    loanType: 'Business Loan',
    aggregatorName: 'Paisabazaar',
  },
  {
    id: 'demo-5',
    type: 'status_update',
    title: 'Application Submitted',
    message: 'Your Business Loan application of ₹10,00,000 was submitted to Paisabazaar.',
    timestamp: new Date(Date.now() - 28 * 3600000).toISOString(),
    read: true,
    loanType: 'Business Loan',
    aggregatorName: 'Paisabazaar',
    amount: '₹10,00,000',
  },
  {
    id: 'demo-6',
    type: 'rejection',
    title: 'Application Not Approved',
    message: 'Your Vehicle Loan application with MyLoanCare was not approved at this time. You may reapply after 30 days.',
    timestamp: new Date(Date.now() - 5 * 86400000).toISOString(),
    read: true,
    loanType: 'Vehicle Loan',
    aggregatorName: 'MyLoanCare',
  },
  {
    id: 'demo-7',
    type: 'status_update',
    title: 'Application Submitted',
    message: 'Your Vehicle Loan application of ₹8,00,000 was submitted to MyLoanCare.',
    timestamp: new Date(Date.now() - 6 * 86400000).toISOString(),
    read: true,
    loanType: 'Vehicle Loan',
    aggregatorName: 'MyLoanCare',
    amount: '₹8,00,000',
  },
];

// ─── Notification Item ────────────────────────────────────────────────────────

function NotificationItem({
  notification,
  onMarkRead,
}: {
  notification: Notification;
  onMarkRead: (id: string) => void;
}) {
  const config = getNotificationConfig(notification.type);

  return (
    <div
      className={`relative flex gap-4 p-4 rounded-2xl border transition-all duration-200 cursor-pointer group
        ${notification.read
          ? 'bg-card border-border hover:border-primary/20 hover:shadow-sm'
          : `bg-blue-50/40 border-blue-100 border-l-4 ${config.accent} hover:shadow-sm`
        }`}
      onClick={() => !notification.read && onMarkRead(notification.id)}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${config.iconBg}`}>
        {config.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-semibold leading-snug ${notification.read ? 'text-foreground' : 'text-foreground'}`}>
              {notification.title}
            </p>
            {!notification.read && (
              <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${config.dot}`} />
            )}
          </div>
          <span className="text-xs text-muted-foreground flex-shrink-0 mt-0.5">
            {formatRelativeTime(notification.timestamp)}
          </span>
        </div>

        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{notification.message}</p>

        {/* Tags */}
        {(notification.loanType || notification.aggregatorName) && (
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {notification.loanType && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                <IndianRupee size={10} />
                {notification.loanType}
              </span>
            )}
            {notification.aggregatorName && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                {notification.aggregatorName}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function NotificationsPage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [isLive, setIsLive] = useState(false);

  // Keep a ref to aggregators so the realtime handler can access them without stale closure
  const aggsRef = useRef<any[]>([]);
  const appsRef = useRef<any[]>([]);

  const supabase = createClient();

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications(DEMO_NOTIFICATIONS);
      setLoading(false);
      return;
    }

    try {
      const [appsRes, aggsRes] = await Promise.all([
        supabase
          .from('user_loan_applications')
          .select('*')
          .eq('user_id', user.id)
          .order('applied_at', { ascending: false }),
        supabase.from('aggregators').select('id, name'),
      ]);

      const apps = appsRes.data || [];
      const aggs = aggsRes.data || [];

      aggsRef.current = aggs;
      appsRef.current = apps;

      if (apps.length === 0) {
        setNotifications(DEMO_NOTIFICATIONS);
      } else {
        setNotifications(buildNotificationsFromApplications(apps, aggs));
      }
    } catch {
      setNotifications(DEMO_NOTIFICATIONS);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    if (!authLoading) fetchNotifications();
  }, [authLoading, fetchNotifications]);

  // ─── Realtime subscription ────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`notifications_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_loan_applications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // New application inserted — add it to our local apps list and rebuild
          const newApp = payload.new as any;
          appsRef.current = [newApp, ...appsRef.current];
          setNotifications(buildNotificationsFromApplications(appsRef.current, aggsRef.current));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_loan_applications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // Application status updated — replace the record and rebuild
          const updatedApp = payload.new as any;
          appsRef.current = appsRef.current.map((app) =>
            app.id === updatedApp.id ? updatedApp : app
          );
          setNotifications(buildNotificationsFromApplications(appsRef.current, aggsRef.current));
        }
      )
      .subscribe((status) => {
        setIsLive(status === 'SUBSCRIBED');
      });

    return () => {
      supabase.removeChannel(channel);
      setIsLive(false);
    };
  }, [user]);

  const handleMarkRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const filtered = notifications.filter((n) => {
    if (activeFilter === 'unread') return !n.read;
    if (activeFilter === 'status') return ['status_update', 'approval', 'rejection', 'under_review'].includes(n.type);
    if (activeFilter === 'messages') return n.type === 'lender_message' || n.type === 'new_offer';
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.read).length;
  const grouped = groupByDate(filtered);

  const FILTER_TABS: { id: FilterTab; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: `Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}` },
    { id: 'status', label: 'Status Updates' },
    { id: 'messages', label: 'Messages & Offers' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <DirectoryTopbar />

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => router.back()}
            className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2.5">
              <h1 className="page-title">Notifications</h1>
              {unreadCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold bg-accent text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-muted-foreground">
                Real-time updates on your loan applications
              </p>
              {user && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                    isLive
                      ? 'bg-green-100 text-green-700' :'bg-muted text-muted-foreground'
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isLive ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground'
                    }`}
                  />
                  {isLive ? 'Live' : 'Connecting…'}
                </span>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 text-sm text-primary font-medium hover:underline flex-shrink-0"
            >
              <CheckCheck size={15} />
              Mark all read
            </button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-1 bg-muted rounded-xl p-1 mb-6 overflow-x-auto">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`flex-shrink-0 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                activeFilter === tab.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 size={28} className="text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">Loading notifications…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
              <Bell size={28} className="text-muted-foreground" />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground">No notifications</p>
              <p className="text-sm text-muted-foreground mt-1">
                {activeFilter === 'unread' ? "You're all caught up! No unread notifications."
                  : 'Notifications about your loan applications will appear here.'}
              </p>
            </div>
            {activeFilter !== 'all' && (
              <button
                onClick={() => setActiveFilter('all')}
                className="text-sm text-primary font-medium hover:underline"
              >
                View all notifications
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map(({ label, items }) => (
              <section key={label}>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {label}
                  </h2>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">{items.length}</span>
                </div>
                <div className="space-y-2.5">
                  {items.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onMarkRead={handleMarkRead}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Footer note */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-center gap-2 mt-8 py-4 border-t border-border">
            <AlertCircle size={13} className="text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              Notifications are retained for 30 days
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
